import { useCallback } from 'react';
import { trackAnalyticsEvent, trackFantasyEvent } from '@/analytics';
import type { Player } from '@dreamteam-monorepo/ui';

export type Ga4EventParams = Record<string, string | undefined>;
export const GA4_GAME_ID = 'dtfantasy';

export const trackGa4Event = (name: string, params?: Ga4EventParams) => {
  trackAnalyticsEvent(name, params);
};

interface FantasyGa4EventOptions {
  eventCategory: string;
  eventLabel: string;
  eventAction?: string;
  extraParams?: Ga4EventParams;
}

export const trackFantasyGa4Event = (
  eventNameSuffix: string,
  { eventCategory, eventLabel, eventAction = 'Click', extraParams }: FantasyGa4EventOptions
) => {
  trackGa4Event(`${GA4_GAME_ID}_${eventNameSuffix}`, {
    section_reference: GA4_GAME_ID,
    [`${GA4_GAME_ID}_interaction`]: eventAction,
    event_category: eventCategory,
    event_action: eventAction,
    event_label: eventLabel,
    ...extraParams,
  });
};

export const getFantasyPlayerAnalyticsParams = (
  player: Pick<Player, 'id' | 'displayName' | 'position' | 'clubName' | 'squadId'>,
  prefix = 'player'
): Ga4EventParams => ({
  [`${prefix}_id`]: String(player.id),
  [`${prefix}_name`]: player.displayName,
  [`${prefix}_position`]: player.position,
  [`${prefix}_team`]: player.clubName ?? String(player.squadId),
});

type FantasyTransferAnalyticsPlayer = Pick<Player, 'id' | 'displayName'>;

export const getFantasyTransferAnalyticsParams = (
  transfers: Array<{
    // The confirm flow passes `outPlayerId` (id only); some callers pass a full
    // `outPlayer` object. Accept both so tracking can never throw and block the
    // transfer (which is what happened when only `outPlayerId` was present).
    outPlayer?: FantasyTransferAnalyticsPlayer;
    outPlayerId?: Player['id'];
    inPlayer?: FantasyTransferAnalyticsPlayer;
  }>
): Ga4EventParams => {
  const incomingPlayers = transfers.flatMap(transfer =>
    transfer.inPlayer ? [transfer.inPlayer] : []
  );

  return {
    transfer_count: String(transfers.length),
    player_out_ids: transfers
      .map(transfer => String(transfer.outPlayer?.id ?? transfer.outPlayerId ?? ''))
      .join('|'),
    player_out_names: transfers.map(transfer => transfer.outPlayer?.displayName ?? '').join('|'),
    player_in_ids: incomingPlayers.map(player => String(player.id)).join('|'),
    player_in_names: incomingPlayers.map(player => player.displayName).join('|'),
  };
};

export const trackFantasyRulesLink = (linkText: string, _extraParams?: Ga4EventParams) => {
  trackFantasyEvent('howToPlayTextLink', {
    link_text: linkText,
  });
};

const HOW_TO_SCORE_TAB_LABELS: Record<string, string> = {
  'all-players': 'All players',
  'gk-def': 'GK & DEF',
  'bonus-pts': 'Bonus points',
};

export const trackFantasyHowToScoreTab = (tabId: string) => {
  trackFantasyRulesLink(`How to score - ${HOW_TO_SCORE_TAB_LABELS[tabId] ?? tabId}`, {
    how_to_score_tab: tabId,
  });
};

export const trackFantasyHowToScoreClose = () => {
  trackFantasyRulesLink('Close how to score');
};

export const trackFantasyGameKeys = (expanded: boolean) => {
  trackFantasyRulesLink(`Game keys - ${expanded ? 'Expand' : 'Collapse'}`, {
    game_keys_state: expanded ? 'expanded' : 'collapsed',
  });
};

export const trackFantasyTeamNameSubmission = (teamName: string) => {
  trackFantasyEvent('onboardingTeamNameConfirmed', {
    team_name: teamName,
  });
};

export const useGa4Tracking = () => {
  const trackEvent = useCallback((name: string, params?: Ga4EventParams) => {
    trackGa4Event(name, params);
  }, []);

  return { trackEvent };
};
